import React from "react";
import { Button, Tooltip, Modal, Dropdown } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import styled, { createGlobalStyle } from "styled-components";
import { useExportWithTracking } from "../hooks/useExportWithTracking";
import {
  WHITE,
  BLUE_HOVER_BUTTON,
  BLACK,
  BLUE_DISABLED_BUTTON,
  BLUE_DISABLED_BUTTON_COLOR,
  BLUE_250
} from "../constant/colors";
import {
  MAX_EXPORT_RECORDS,
  FILE_TYPES,
  EXCLUDED_EXPORT_COLUMNS,
  DEFAULT_CONTENT_KEY,
  DEFAULT_CONTENT_TYPE
} from "../constant/export";
import { selectTotalPatients, selectSorting, selectFilters, selectPatientTableColumns } from "../store/selector";
import { ReactComponent as WarningIcon } from "../assets/svg/warning-icon-popup.svg";
import { ReactComponent as ExportIcon } from "../assets/svg/export-icon.svg";
import LoadingIcon from "./LoadingIcon";
import { ExportModalContent } from "./ExportUtils";
import { fontWeights } from "../constant/styles";

const ExportDropdownStyles = createGlobalStyle`
  .export-dropdown-menu {
    margin-left: 4px;
    .ant-dropdown-menu {
      border-radius: 8px;
      min-width: 116px;
      padding: 0;
    }
    .ant-dropdown-menu-item {
      border-radius: 4px;
    }
    .ant-dropdown-menu-item:first-child {
      border-radius: 8px 8px 0 0;
    }
    .ant-dropdown-menu-item:last-child {
      border-radius: 0 0 8px 8px;
    }
  }
`;

const StyledDropdownButton = styled(Button)`
  height: 32px;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: ${fontWeights.semibold};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-color: ${BLUE_250};
  color: ${BLUE_250};

  .anticon {
    font-size: 16px;
    font-weight: 700;
    width: 18px;
    height: 18px;
  }

  svg {
    width: 16px;
    height: 16px;
    display: inline-block;
    position: relative;
    top: 2px;
  }

  &.loading-state svg {
    top: 0;
  }

  .anticon {
    top: 0;
  }

  & > .anticon + span,
  & > span + .anticon {
    margin-left: 10px;
  }

  &.loading-state > span {
    display: flex;
    align-items: center;
  }

  &:active,
  &:focus {
    color: ${BLUE_250} !important;
    border-color: ${BLUE_250} !important;
    background-color: transparent !important;
    box-shadow: 0 0 0 2px rgba(44, 86, 163, 0.3) !important;
    outline: none !important;
    opacity: 1 !important;
  }

  &:hover:not(:disabled):not(:focus):not(:active) {
    color: ${BLUE_HOVER_BUTTON} !important;
    border-color: ${BLUE_HOVER_BUTTON} !important;
  }

  &:disabled,
  &[disabled] {
    cursor: not-allowed;

    &:not(.loading-state) {
      background: ${BLUE_DISABLED_BUTTON} !important;
      border-color: ${BLUE_DISABLED_BUTTON} !important;
      color: ${BLUE_DISABLED_BUTTON_COLOR} !important;

      .anticon {
        color: ${BLUE_DISABLED_BUTTON_COLOR} !important;
      }

      &:hover {
        background: ${BLUE_DISABLED_BUTTON} !important;
        border-color: ${BLUE_DISABLED_BUTTON} !important;
        color: ${BLUE_DISABLED_BUTTON_COLOR} !important;
      }
    }

    &.loading-state {
      background: ${WHITE} !important;
      border-color: ${BLUE_250} !important;
      color: ${BLUE_250} !important;

      &:hover {
        background: ${WHITE} !important;
        border-color: ${BLUE_250} !important;
        color: ${BLUE_250} !important;
      }
    }
  }
`;

const ExportButtonComponent = ({ t }) => {
  const [modal, contextHolder] = Modal.useModal();
  const { exportWithTracking, isExporting } = useExportWithTracking();

  const totalPatients = useSelector(selectTotalPatients);
  const sorting = useSelector(selectSorting);
  const filters = useSelector(selectFilters);
  const patientTableColumns = useSelector(selectPatientTableColumns);

  // Handle edge cases: undefined, null, negative numbers
  const validRecordCount = typeof totalPatients === "number" && totalPatients >= 0 ? totalPatients : 0;
  const isDisabledByRecordCount = validRecordCount > MAX_EXPORT_RECORDS;
  const isButtonDisabled = isDisabledByRecordCount || isExporting || validRecordCount === 0;

  const getTooltipText = () => {
    if (validRecordCount > MAX_EXPORT_RECORDS) {
      return t("export.record_limit_exceeded_tooltip", { limit: MAX_EXPORT_RECORDS.toLocaleString() });
    }
    return "";
  };

  const handleExport = async (fileType) => {
    const visibleColumns = Object.keys(patientTableColumns || {}).filter(
      (key) => patientTableColumns[key]?.display && !EXCLUDED_EXPORT_COLUMNS.includes(key)
    );

    await exportWithTracking(
      {
        listType: "pharmacy",
        selectedView: "pharmacy-list",
        filters,
        sorting,
        visibleColumns,
        savedListId: null,
        fileType
      },
      {
        pageKey: "pharmacy-list",
        contentKey: DEFAULT_CONTENT_KEY,
        contentType: DEFAULT_CONTENT_TYPE,
        exportType: fileType,
        numberOfRecords: validRecordCount,
        sorting,
        filters,
        visibleColumns,
        isSavedList: false,
        savedListId: null,
        savedListName: null,
        savedListOriginBucket: null
      }
    );
  };

  const handleExportClick = (fileType) => {
    const modalInstance = modal.confirm({
      centered: true,
      className: "formPopupsModal",
      okText: t("export.modal_confirm_button"),
      cancelText: t("cancel"),
      icon: <WarningIcon />,
      title: t("export.modal_title"),
      content: <ExportModalContent count={validRecordCount} fileType={fileType} />,
      okButtonProps: {
        loading: false
      },
      onOk: () => {
        modalInstance.destroy();
        handleExport(fileType);
      },
      onCancel: () => {
        // Modal closes automatically
      }
    });
  };

  const handleMenuClick = ({ key }) => {
    handleExportClick(key);
  };

  const menuItems = [
    {
      key: FILE_TYPES.CSV,
      label: t("export.csv_option")
    },
    {
      key: FILE_TYPES.XLSX,
      label: t("export.xlsx_option")
    }
  ];

  const buttonContent = (
    <>
      <ExportDropdownStyles />
      <Dropdown
        menu={{
          items: menuItems,
          onClick: handleMenuClick
        }}
        disabled={isButtonDisabled}
        trigger={["click"]}
        overlayClassName="export-dropdown-menu"
      >
        <StyledDropdownButton
          id="download_pharmacy_report"
          type="default"
          disabled={isButtonDisabled}
          className={isExporting ? "loading-state" : ""}
          aria-label={t("export.export_list")}
          aria-disabled={isButtonDisabled}
          aria-busy={isExporting}
          onClick={(e) => e.stopPropagation()}
        >
          {isExporting ? (
            <>
              <LoadingIcon />
              <span style={{ marginLeft: "8px" }}>{t("export.loading_text")}</span>
            </>
          ) : (
            <>
              <ExportIcon />
              <span style={{ marginLeft: "8px" }}>{t("export.button_text")}</span>
              <DownOutlined style={{ fontSize: "14px", width: "14px", height: "14px", marginLeft: "10px" }} />
            </>
          )}
        </StyledDropdownButton>
      </Dropdown>
    </>
  );

  return (
    <>
      {isButtonDisabled && !isExporting ? (
        <Tooltip
          title={getTooltipText()}
          placement="topRight"
          arrow={{ pointAtCenter: false }}
          overlayInnerStyle={{
            padding: "10px 10px",
            width: "330px",
            color: BLACK,
            backgroundColor: WHITE,
            fontWeight: 400,
            fontSize: 12
          }}
          overlayStyle={{ maxWidth: "450px", borderRadius: "8px" }}
          color={WHITE}
        >
          <span style={{ display: "inline-block" }}>{buttonContent}</span>
        </Tooltip>
      ) : (
        buttonContent
      )}
      {contextHolder}
    </>
  );
};

const ExportButton = withTranslation()(ExportButtonComponent);

export { ExportButton };
